import type { RulesRepository } from '@/rules-management/domain/RulesRepository.ts';
import type {
  Rule,
  RuleId,
  RuleStatus,
  RulesFilter,
  RulesRepository as RuleRulesRepository,
} from '@/rule';
import { type Page, type Pagination, paginationToQuery } from '@/generic/pagination';
import type { AuthenticatedFetcher } from '@/generic/authentication';
import type { RulesFileToImport } from '@/rules-management/domain/RulesFileToImport.ts';
import { parseJsonToRuleExtractionId } from '@/rules-management/secondary/RuleExtractionJson.ts';
import {
  type ListenExtraction,
  type RuleExtraction,
  RuleExtractionCompleted,
  RuleExtractionFailed,
  RuleExtractionId,
  RuleExtractionInProgress,
} from '@/rules-management/domain/RuleExtraction.ts';
import { Poll } from '@/generic/polling';
import type { DraftRule, DraftRuleEdit, DraftRuleId } from '@/rules-management/domain/DraftRule.ts';
import {
  draftRuleEditToJson,
  parseJsonToDraftRule,
  parseJsonToDraftRulesPage,
} from '@/rules-management/secondary/DraftRuleJson.ts';
import { RulesHaveBeenPublished } from '@/rules-management/domain/RulesHaveBeenPublished.ts';
import { DraftRulesHaveChanged } from '@/rules-management/domain/DraftRulesHaveChanged.ts';
import type { Unsubscribe } from '@/rules-management/domain/Unsubscribe.ts';

const EXTRACTION_EVENT = 'rules-management.extraction';

const RULES_PUBLISHED_EVENT = 'rules-management.rules.published';

const DRAFT_RULES_CHANGED_EVENT = 'rules-management.draft-rules.changed';

const IN_PROGRESS_STATUS = 202;

const MAX_CONSECUTIVE_POLL_FAILURES = 3;

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

export class RulesRepositoryRule implements RulesRepository {
  readonly #ruleRulesRepository: RuleRulesRepository;
  readonly #fetcher: AuthenticatedFetcher;
  readonly #pool: Poll;
  readonly #consecutivePollFailures: Map<string, number>;

  constructor(
    ruleRulesRepository: RuleRulesRepository,
    fetcher: AuthenticatedFetcher,
    pollingDuration: number,
  ) {
    this.#ruleRulesRepository = ruleRulesRepository;
    this.#fetcher = fetcher;
    this.#pool = Poll.of(pollingDuration);
    this.#consecutivePollFailures = new Map();
    this.#pool.run();
  }

  async list(pagination: Pagination, filter: RulesFilter): Promise<Page<Rule>> {
    return this.#ruleRulesRepository.list(pagination, filter);
  }

  async updateStatus(id: RuleId, status: RuleStatus): Promise<Rule> {
    return this.#ruleRulesRepository.updateStatus(id, status);
  }

  async extract(file: RulesFileToImport): Promise<RuleExtractionId> {
    const formData = new FormData();
    formData.append('file', file.file);
    return await this.#fetcher
      .fetch('/api/rule-extractions', {
        method: 'POST',
        body: formData,
      })
      .then(parseJsonToRuleExtractionId);
  }

  private async extraction(id: RuleExtractionId): Promise<RuleExtraction> {
    const response = await this.#fetcher.raw(`/api/rule-extractions/${id.value}`, {
      method: 'GET',
    });
    if (response.status === IN_PROGRESS_STATUS) {
      return RuleExtractionInProgress.of(id);
    }
    return RuleExtractionCompleted.of(id);
  }

  #stopFollowingExtraction(id: RuleExtractionId): void {
    this.#pool.remove(id.value);
    this.#consecutivePollFailures.delete(id.value);
  }

  listenExtraction(id: RuleExtractionId): ListenExtraction {
    const eventName = `${EXTRACTION_EVENT}.${id.value}`;

    this.#consecutivePollFailures.set(id.value, 0);
    this.#pool.set(id.value, async () => {
      try {
        const extraction = await this.extraction(id);
        this.#consecutivePollFailures.set(id.value, 0);
        globalThis.dispatchEvent(new CustomEvent(eventName, { detail: extraction }));
        if (!(extraction instanceof RuleExtractionInProgress)) {
          this.#stopFollowingExtraction(id);
        }
      } catch (error) {
        const failures = (this.#consecutivePollFailures.get(id.value) ?? 0) + 1;
        this.#consecutivePollFailures.set(id.value, failures);
        if (failures < MAX_CONSECUTIVE_POLL_FAILURES) {
          return;
        }
        globalThis.dispatchEvent(
          new CustomEvent(eventName, { detail: RuleExtractionFailed.of(id, toError(error)) }),
        );
        this.#stopFollowingExtraction(id);
      }
    });

    return (onChange) => {
      const listenEvent = (event: Event) => onChange((event as CustomEvent<RuleExtraction>).detail);

      globalThis.addEventListener(eventName, listenEvent);

      return () => {
        globalThis.removeEventListener(eventName, listenEvent);
      };
    };
  }

  async listDrafts(pagination: Pagination): Promise<Page<DraftRule>> {
    return await this.#fetcher
      .fetch('/api/draft-rules', {
        method: 'GET',
        query: paginationToQuery(pagination),
      })
      .then(parseJsonToDraftRulesPage);
  }

  async publish(id: DraftRuleId): Promise<void> {
    await this.#fetcher.fetch(`/api/draft-rules/${id.value}/publish`, { method: 'POST' });
  }

  async discard(id: DraftRuleId): Promise<void> {
    await this.#fetcher.fetch(`/api/draft-rules/${id.value}`, { method: 'DELETE' });
  }

  async update(id: DraftRuleId, edit: DraftRuleEdit): Promise<DraftRule> {
    return await this.#fetcher
      .fetch(`/api/draft-rules/${id.value}`, {
        method: 'PATCH',
        body: draftRuleEditToJson(edit),
      })
      .then(parseJsonToDraftRule);
  }

  notifyRulesPublished(): void {
    globalThis.dispatchEvent(
      new CustomEvent(RULES_PUBLISHED_EVENT, { detail: RulesHaveBeenPublished }),
    );
  }

  onRulesPublished(listener: (event: RulesHaveBeenPublished) => void): Unsubscribe {
    const handler = (event: Event) =>
      listener((event as CustomEvent<RulesHaveBeenPublished>).detail);
    globalThis.addEventListener(RULES_PUBLISHED_EVENT, handler);

    return () => {
      globalThis.removeEventListener(RULES_PUBLISHED_EVENT, handler);
    };
  }

  notifyDraftRulesChanged(): void {
    globalThis.dispatchEvent(
      new CustomEvent(DRAFT_RULES_CHANGED_EVENT, { detail: DraftRulesHaveChanged }),
    );
  }

  onDraftRulesChanged(listener: (event: DraftRulesHaveChanged) => void): Unsubscribe {
    const handler = (event: Event) =>
      listener((event as CustomEvent<DraftRulesHaveChanged>).detail);
    globalThis.addEventListener(DRAFT_RULES_CHANGED_EVENT, handler);

    return () => {
      globalThis.removeEventListener(DRAFT_RULES_CHANGED_EVENT, handler);
    };
  }
}
